/**
 * HOUSEHOLDS AND THE NUMBERS BUILT ON THEM, for one bag group, from the group
 * document alone (no Firebase, so the page, the plain node tests and the
 * render harnesses all import it). Moved out of bagGroups.ts for BG-8, which
 * re-exports every name here, so existing imports are unchanged.
 *
 * A household is a shared dancer id in memberSummaries[].dancers, never a
 * surname (bag group plan v2, BG-8 traps). The chart's rows join members
 * transitively; who may act, and who a to-do is for, is direct sharing only
 * (see TWO QUESTIONS below). The item lane imports this file too, so the
 * page and the server share both definitions.
 */
import { deriveFamilyName, type FamilyUnitStudent } from './familyGroups.ts';

/**
 * The parts of a member entry these helpers read: structurally
 * BagGroupMemberSummary and BagGroupMemberDancer in bagGroups.ts. Spelled out
 * here rather than imported, because the item lane loads this file on the
 * server and bagGroups.ts is the browser's Firebase module.
 */
interface BagGroupMemberDancer {
  id: string;
  name: string;
}
interface BagGroupMemberSummary {
  uid: string;
  displayName: string;
  dancerNames: string[];
  dancers?: BagGroupMemberDancer[];
  noAccount?: boolean;
  isLead?: boolean;
}

function lastNameToken(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return parts.length > 0 ? parts[parts.length - 1] : '';
}

/**
 * deriveFamilyName() (familyGroups.ts, Manage Users' own derivation) always
 * returns lowercase "... family", or the bare literal "Family" when nothing
 * usable was found at all. Bobbie picked "Ford Family", capital F, for the
 * bag group heading specifically - Manage Users was never asked about and
 * must not move as a side effect of this one screen's answer, so this
 * capitalizes ONLY the string this call site prints, never the shared
 * helper.
 *
 * The regex requires a literal space before lowercase "family", which the
 * bare "Family" fallback never has (no leading space, already capital F),
 * so that path is left alone - it can never become "Family Family" or lose
 * its own capital letter. Every other path (a single surname, or the
 * two-way "X & Y family" tie) always ends in " family" and gets the swap.
 */
function capitalizeFamilyHeading(name: string): string {
  return name.replace(/ family$/, ' Family');
}

/**
 * TWO QUESTIONS, TWO FUNCTIONS, one module (BG-8 refute rounds 1 and 2).
 *
 *   ROWS (bagHouseholdRowIndexes): which members the Season chart draws as
 *   one Family row. Transitive: a chain of shared dancer ids joins them, so a
 *   blended family (A has Ava; B has Ava and Ben; C has Ben) is one row.
 *
 *   ACTING (bagActingHouseholdOf, sharesBagActingHousehold): who may act on
 *   an item for its owner, and who her to-do is for. DIRECT only, Bobbie's
 *   ruling 5, "either parent": a member whose own entry shares at least one
 *   dancer id with the owner's entry. A transitive reach went further than
 *   that ruling: a grandparent listed on two families (Ava and Zed) chained
 *   Ava's parent to Zed's, and one family's to-do landed on the other. So C
 *   in the blended case sees the row but acts only on items whose owner
 *   shares a dancer with her (recorded in the follow-ups doc).
 *
 * Never a surname. Shape-loose on purpose: the item lane hands these raw
 * memberSummaries entries (netlify/functions/lib/bagGroupItemLane.mjs and
 * bagGroupItemTodo.mjs import this file), the page hands normalized ones.
 */
export interface BagHouseholdEntryLike {
  uid?: unknown;
  noAccount?: unknown;
  dancers?: unknown;
}

function householdDancerIds(entry: BagHouseholdEntryLike): string[] {
  if (!Array.isArray(entry.dancers)) return [];
  const ids: string[] = [];
  for (const dancer of entry.dancers) {
    const id = dancer && typeof dancer === 'object' ? (dancer as { id?: unknown }).id : undefined;
    if (typeof id === 'string' && id) ids.push(id);
  }
  return ids;
}

/**
 * ROWS ONLY. The chart's Family rows among these entries, as lists of entry
 * indexes: every entry with at least one dancer id, joined transitively
 * through shared ids. Each list is in entry order, and the lists are ordered
 * by their first entry. An entry with no dancer id belongs to no row here.
 * Never use this to decide who may act or who a to-do is for.
 */
export function bagHouseholdRowIndexes(entries: readonly BagHouseholdEntryLike[]): number[][] {
  const parent = entries.map((_, index) => index);
  const find = (x: number): number => {
    while (parent[x] !== x) {
      parent[x] = parent[parent[x]];
      x = parent[x];
    }
    return x;
  };
  const firstSeenAt = new Map<string, number>();
  const scoped: number[] = [];
  entries.forEach((entry, index) => {
    const ids = householdDancerIds(entry);
    if (ids.length === 0) return;
    scoped.push(index);
    for (const id of ids) {
      const seenAt = firstSeenAt.get(id);
      if (seenAt === undefined) {
        firstSeenAt.set(id, index);
        continue;
      }
      const ra = find(seenAt);
      const rb = find(index);
      if (ra !== rb) parent[Math.max(ra, rb)] = Math.min(ra, rb);
    }
  });
  const byRoot = new Map<number, number[]>();
  for (const index of scoped) {
    const root = find(index);
    const members = byRoot.get(root);
    if (members) members.push(index);
    else byRoot.set(root, [index]);
  }
  return [...byRoot.values()];
}

/**
 * ACTING. The owner and every real account (never a no-account dancer row)
 * whose own entry shares at least one dancer id DIRECTLY with the owner's
 * entry, owner first, plus the owner's own dancer ids. An owner with no
 * dancer on her entry acts alone; someone with no real entry gets nothing.
 */
export function bagActingHouseholdOf(
  entries: readonly BagHouseholdEntryLike[],
  ownerUid: string
): { uids: string[]; dancerIds: string[] } {
  const owner = entries.find((entry) => entry.uid === ownerUid && entry.noAccount !== true);
  if (!ownerUid || !owner) return { uids: [], dancerIds: [] };
  const dancerIds = [...new Set(householdDancerIds(owner))];
  const mine = new Set(dancerIds);
  const uids = [ownerUid];
  if (mine.size === 0) return { uids, dancerIds };
  for (const entry of entries) {
    const uid = typeof entry.uid === 'string' ? entry.uid : '';
    if (!uid || entry.noAccount === true || uids.includes(uid)) continue;
    if (householdDancerIds(entry).some((id) => mine.has(id))) uids.push(uid);
  }
  return { uids, dancerIds };
}

/**
 * ACTING. Ruling 5, "either parent": the owner herself, or a real member
 * whose entry shares a dancer id directly with the owner's. The lane decides
 * with this; the page uses it only to choose which buttons to draw.
 */
export function sharesBagActingHousehold(
  entries: readonly BagHouseholdEntryLike[],
  uid: string,
  ownerUid: string | null | undefined
): boolean {
  if (!ownerUid || !uid) return false;
  if (ownerUid === uid) return true;
  return bagActingHouseholdOf(entries, ownerUid).uids.includes(uid);
}

export interface BagGroupHousehold {
  /** Sorted, joined dancer ids - stable across renames, same idea as
   * FamilyUnit.key in familyGroups.ts. */
  key: string;
  name: string;
  dancers: BagGroupMemberDancer[];
  parents: Array<{ uid: string; displayName: string; isLead: boolean }>;
}

/**
 * Group THIS group's own members into households (Bobbie, 2026-09-01,
 * picking the family-headed layout over one repeated under each dancer:
 * "Ford Family / Nora Ford - Dancer / Bobbie Ford - Family / Zach Ford -
 * Family"). Two members are the same household when they share a dancer -
 * derived entirely from this one group document, never a studio-wide
 * roster fetch, so a family account reading its own group never needs read
 * access to anyone else's dancer.
 *
 * That is also why only members the dancer picker actually wrote (a
 * `dancers` list) can be grouped: a pre-pivot or staff-added entry carries
 * no dancer id to union on, so it renders on its own in `ungrouped` rather
 * than a guessed household.
 *
 * The family name is deriveFamilyName() from familyGroups.ts, the same
 * derivation the Manage Users page already uses and already has its own
 * test file - never invented here. This document only ever stored
 * "First Last" strings, so a dancer's own last name (the last token of that
 * string) stands in for the lastName field deriveFamilyName expects - the
 * same fallback it already uses for a parent with no dancer to count.
 * capitalizeFamilyHeading() then title-cases just the word "family" for
 * THIS heading (Bobbie picked capital-F "Ford Family" when shown both) -
 * see its own comment for why that stays local to this call site rather
 * than touching the shared helper Manage Users also calls.
 */
export function groupBagGroupHouseholds<M extends BagGroupMemberSummary>(members: readonly M[]): {
  households: BagGroupHousehold[];
  ungrouped: M[];
} {
  const scoped = members.filter((member): member is M & { dancers: BagGroupMemberDancer[] } =>
    Array.isArray(member.dancers) && member.dancers.length > 0
  );
  const ungrouped = members.filter((member) => !(Array.isArray(member.dancers) && member.dancers.length > 0));

  // ROWS: the transitive grouping, for drawing only (bagHouseholdRowIndexes).
  const households: BagGroupHousehold[] = [];
  for (const indices of bagHouseholdRowIndexes(scoped)) {
    const memberRows = indices.map((index) => scoped[index]);
    const dancersById = new Map<string, BagGroupMemberDancer>();
    for (const member of memberRows) {
      for (const dancer of member.dancers) {
        if (!dancersById.has(dancer.id)) dancersById.set(dancer.id, dancer);
      }
    }
    const dancers = [...dancersById.values()];
    const familyStudents: FamilyUnitStudent[] = dancers.map((dancer) => ({
      id: dancer.id,
      firstName: '',
      lastName: lastNameToken(dancer.name),
      onCompTeam: true,
      // Bag groups only use FamilyUnitStudent for deriveFamilyName's naming
      // maths, which never reads this field - Manage Users' family band is
      // the only consumer that cares, and this is not that surface.
      jrStaffDesignated: false,
    }));
    const parents = memberRows
      .filter((member) => member.noAccount !== true)
      .map((member) => ({ uid: member.uid, displayName: member.displayName, isLead: member.isLead === true }));
    const parentNames = parents.map((parentRow) => parentRow.displayName);
    households.push({
      key: [...dancersById.keys()].sort().join('|'),
      name: capitalizeFamilyHeading(deriveFamilyName(familyStudents, parentNames)),
      dancers,
      parents,
    });
  }
  households.sort((a, b) => a.name.localeCompare(b.name));
  return { households, ungrouped };
}

/**
 * ACTING, the very function the item lane decides with
 * (sharesBagActingHousehold; sharesBagHousehold in
 * netlify/functions/lib/bagGroupItemLane.mjs calls it too; ruling 5, "either
 * parent", direct sharing only). Never a surname. The lane decides; this only
 * chooses which buttons to draw.
 */
export function sharesBagGroupHousehold(
  members: readonly BagGroupMemberSummary[],
  uid: string,
  ownerUid: string | null | undefined
): boolean {
  return sharesBagActingHousehold(members, uid, ownerUid);
}

/**
 * Dancers in the group, deduped by dancer id (a dancer with two signed-in
 * parents is one dancer). A pre-pivot entry has no id to dedupe by, so it
 * falls back to its stored names, best effort.
 */
export function bagGroupDancerCount(members: readonly BagGroupMemberSummary[]): number {
  const ids = new Set<string>();
  const legacyNames = new Set<string>();
  for (const member of members) {
    if (member.dancers) {
      for (const dancer of member.dancers) ids.add(dancer.id);
    } else {
      for (const name of member.dancerNames) legacyNames.add(name);
    }
  }
  return ids.size + legacyNames.size;
}

/**
 * A new item's quantity: one for each dancer in the group (plan 3.6), the
 * same on the item form and the Season chart. A group with no dancer yet
 * starts at 1, the chart's long-standing fallback.
 */
export function bagGroupDefaultQuantity(members: readonly BagGroupMemberSummary[]): string {
  return String(bagGroupDancerCount(members) || 1);
}

/**
 * The hero line, "N dancers, M families, X of Y dropped off" (plan 3.6).
 * A family is a household (groupBagGroupHouseholds: shared dancer ids), a
 * dancer with no sign-in yet included; an older entry with dancer names but
 * no ids counts as one family of its own. Staff and parents added with no
 * dancer are not families. Dropped off counts items dropped off or received.
 */
export function bagGroupHeroCounts(
  members: readonly BagGroupMemberSummary[],
  items: readonly { status: string }[]
): { dancers: number; families: number; dropped: number; total: number } {
  const { households, ungrouped } = groupBagGroupHouseholds(members);
  return {
    dancers: bagGroupDancerCount(members),
    families: households.length + ungrouped.filter((member) => member.dancerNames.length > 0).length,
    dropped: items.filter((item) => item.status === 'dropped-off' || item.status === 'done').length,
    total: items.length,
  };
}

/**
 * One row of the Season chart: a household (a dancer and her parents, plan
 * 3.6, header "Family"), a member with no dancer on her entry (a staff lead,
 * a parent added directly, an older entry), or the "Nobody yet" row of open
 * items, always last. A household is a shared dancer id
 * (groupBagGroupHouseholds), never a surname.
 */
export interface BagGroupChartRow {
  key: string;
  label: string;
  /** A household's dancers by first name, so two families with one surname read apart. */
  sub?: string;
  /** The accounts whose items fill this row's cells. Empty for Nobody yet. */
  uids: string[];
  /** Who an item made from a tap on this row belongs to: her first parent. */
  ownerUid: string | null;
  nobodyYet?: boolean;
}

export function bagGroupChartRows(members: readonly BagGroupMemberSummary[]): BagGroupChartRow[] {
  const { households, ungrouped } = groupBagGroupHouseholds(members);
  const rows: BagGroupChartRow[] = [];
  // A dancer with no sign-in yet has nobody who could bring or drop off
  // anything, so her household gets no row until a parent is linked (the
  // chart left her out before households too).
  for (const household of households) {
    if (household.parents.length === 0) continue;
    rows.push({
      key: `family:${household.key}`,
      label: household.name,
      sub: household.dancers.map((dancer) => dancer.name.trim().split(/\s+/)[0]).filter(Boolean).join(', '),
      uids: household.parents.map((parent) => parent.uid),
      ownerUid: household.parents[0].uid,
    });
  }
  for (const member of ungrouped) {
    if (member.noAccount === true) continue;
    rows.push({ key: `member:${member.uid}`, label: member.displayName, uids: [member.uid], ownerUid: member.uid });
  }
  rows.push({ key: 'nobody-yet', label: 'Nobody yet', uids: [], ownerUid: null, nobodyYet: true });
  return rows;
}

/**
 * "3 dancers, 2 families, 1 of 4 dropped off". Until the items read has
 * settled with a real answer, the drop-off part is left off rather than
 * shown as a confident "0 of 0".
 */
export function bagGroupHeroCountsText(counts: ReturnType<typeof bagGroupHeroCounts>, itemsConfirmed = true): string {
  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
  const people = `${plural(counts.dancers, 'dancer', 'dancers')}, ${plural(counts.families, 'family', 'families')}`;
  return itemsConfirmed ? `${people}, ${counts.dropped} of ${counts.total} dropped off` : people;
}
